import React from 'react';
import { RefreshCw, Home, AlertTriangle } from 'lucide-react';

// iter88 — App-wide React error boundary. Catches ANY synchronous render error in the tree
// below it and shows a clear, branded fallback INSTEAD of letting React unmount everything
// (which produced black/blank screens in production).
//
// Two boundaries are mounted in App.js:
//   • App-level (wraps the whole router) — catches anything outside a route
//   • Route-level (wraps <Outlet/>-style children inside layouts) — keeps the sidebar visible
//     so the user can navigate away even if one page crashed
//
// Errors are logged to console + (best-effort) shipped to /api/client-errors so we get
// signal in the admin audit log without depending on the user reporting it.
//
// Resetting on route change: the boundary key is the current pathname (set in App.js), so
// navigating away clears the error state automatically.

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('[NexFlow ErrorBoundary]', this.props.scope || 'unknown', error, info);
    // Best-effort: ship to backend so we get audit signal. Never throw from here.
    try {
      const payload = {
        scope: this.props.scope || 'unknown',
        message: (error && error.message) || String(error),
        stack: (error && error.stack) || '',
        component_stack: info?.componentStack || '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        // iter89 — surface SW version + last-known app version for triage.
        app_version: (typeof window !== 'undefined' && window.localStorage)
          ? (window.localStorage.getItem('nexflow_app_version') || '')
          : '',
      };
      fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/client-errors`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch { /* */ }
  }

  reset = () => this.setState({ hasError: false, error: null, info: null });

  goHome = () => {
    this.reset();
    try { window.location.assign('/'); } catch { /* */ }
  };

  hardReload = () => {
    try { window.location.reload(); } catch { /* */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = (this.state.error && this.state.error.message) || 'Something went wrong loading this page.';
    return (
      <div data-testid="error-boundary-fallback" role="alert" aria-live="assertive" className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-[#141414] border border-[#7F1D1D] rounded-lg p-6 space-y-4 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#7F1D1D]/20 border border-[#7F1D1D] flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-xl font-bold text-white">Something went wrong</h2>
              <p className="text-[#A3A3A3] text-xs">{this.props.scope ? `Section: ${this.props.scope}` : 'NexFlow Hub'}</p>
            </div>
          </div>
          <p className="text-sm text-[#D4AF37] break-words">{msg}</p>
          <p className="text-xs text-[#A3A3A3]">
            The rest of the app is still running. You can try reloading this section, return to the dashboard, or refresh the page.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="error-boundary-retry"
              onClick={this.reset}
              className="flex-1 min-w-[120px] bg-[#D4AF37] text-[#0A0A0A] rounded-md px-3 py-2 text-sm font-bold hover:bg-[#F3E5AB] transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
            <button
              data-testid="error-boundary-home"
              onClick={this.goHome}
              className="flex-1 min-w-[120px] border border-[#262626] text-white rounded-md px-3 py-2 text-sm hover:bg-[#262626] transition-colors flex items-center justify-center gap-2"
            >
              <Home className="w-4 h-4" /> Dashboard
            </button>
            <button
              data-testid="error-boundary-reload"
              onClick={this.hardReload}
              className="flex-1 min-w-[120px] border border-[#262626] text-white rounded-md px-3 py-2 text-sm hover:bg-[#262626] transition-colors"
            >
              Refresh page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
